import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getBasicFilteringOptions,
  getClearEvidenceTemplateModel,
  getEvidencesCount,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import AddOrEditEvidenceTemplate from "./components/AddOrEditEvidenceTemplate";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function EvidenceTemplate() {
  const {
    evidenceTemplateModel,
    setEvidenceTemplateModel,
    evidenceTemplateData,
    EVIDENCE_TEMPLATE_ENDPOINT,
    config,
    pageIndexEvidenceTemplate,
    setPageIndexEvidenceTemplate,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [evidenceTemplateDataFiltered, setEvidenceTemplateDataFiltered] =
    useState(evidenceTemplateData.Data);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getBasicFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const operationalDataObj = evidenceTemplateData.Data.find(
        (operationalAreaObj) => operationalAreaObj.Id === Number(id)
      );
      setEvidenceTemplateModel(operationalDataObj);
    }
  }, [id]);

  useEffect(() => {
    setEvidenceTemplateDataFiltered(evidenceTemplateData.Data);
  }, [evidenceTemplateData]);

  const validateOperationResult = (result, isDelete) => {
    setEvidenceTemplateModel(getClearEvidenceTemplateModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([EVIDENCE_TEMPLATE_ENDPOINT, config]);
    if (isDelete) {
      if (
        pageIndexEvidenceTemplate !== 0 &&
        evidenceTemplateModel.Data.length === 1
      ) {
        setPageIndexEvidenceTemplate(pageIndexEvidenceTemplate - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${EVIDENCE_TEMPLATE_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${EVIDENCE_TEMPLATE_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${EVIDENCE_TEMPLATE_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !evidenceTemplateModel?.Name ||
    evidenceTemplateModel?.Name.replaceAll(" ", "").length <= 0 ||
    !evidenceTemplateModel?.Photos ||
    evidenceTemplateModel?.Photos.replaceAll(" ", "") === "[]";

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.evidences,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.evidence_template}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={EVIDENCE_TEMPLATE_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="evidence_template"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_evidence_template}
          isEditForm
          onClickToSubmit={() => {
            handleUpdateRequest(evidenceTemplateModel);
            setShowEditModal(false);
            setEvidenceTemplateModel(getClearEvidenceTemplateModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setEvidenceTemplateModel(getClearEvidenceTemplateModel());
          }}
          Component={
            <AddOrEditEvidenceTemplate
              model={evidenceTemplateModel}
              setModel={setEvidenceTemplateModel}
            />
          }
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.evidence_template}
            subtitle={localize.evidence_template_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="evidence_template"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_evidence_template}
                onClickToSubmit={() => {
                  handleCreateRequest(evidenceTemplateModel);
                  setShowAddModal(false);
                  setEvidenceTemplateModel(getClearEvidenceTemplateModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setEvidenceTemplateModel(getClearEvidenceTemplateModel());
                }}
                Component={
                  <AddOrEditEvidenceTemplate
                    model={evidenceTemplateModel}
                    setModel={setEvidenceTemplateModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={evidenceTemplateModel.Name}
        entityType={localize.evidence_template}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setEvidenceTemplateModel(getClearEvidenceTemplateModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(evidenceTemplateModel.Id);
          setEvidenceTemplateModel(getClearEvidenceTemplateModel());
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexEvidenceTemplate}
            setPageIndex={setPageIndexEvidenceTemplate}
            dataArray={evidenceTemplateData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          evidenceTemplateData.Data?.length !== 0
            ? evidenceTemplateDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setEvidenceTemplateModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setEvidenceTemplateModel(item);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  getEvidencesCount(item),
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(6)]
        }
      />
    </div>
  );
}
